import React, { Suspense, lazy, useState } from "react";
import ClubShield from "./ClubShield";
import { CLUB_SHOWDOWN, SHOWDOWN_TURN, SHOWDOWN_PERSPECTIVE } from "./clubs";
import { BroadcastIcon, PrizeTrophyIcon, ChevronRightIcon } from "./icons";

// Two steps of form behind a user action — kept out of the landing bundle until
// someone actually asks for a challenge.
const ClubChallengeRequestModal = lazy(() => import("./ClubChallengeRequestModal"));

// One crest on the left, one on the right, VS between them. Nothing overlaps
// anything: the badge is a flow item in the same row, not an overlay, so the
// layout holds even if a crest image fails to load.

// College Club Challenges.
//
// ONE component for every state of League Play (empty and populated) and for
// both breakpoints — the tight active-state typography/spacing is the spec, so
// the empty-state page can't drift looser. Only the crest artwork and the copy
// live here; the section heading is the caller's SectionHeader.
const ClubChallengeCard = () => {
  const [modal, setModal] = useState(null); // "explore" | "request" | null

  return (
    <>
      {/* Desktop polish: "Reduce the College Club Challenges card height to about
          300 to 320px." The savings come from the top padding and the stack
          below the crests; mobile is unchanged. */}
      <div className="rounded-2xl bg-white border border-gray-200 px-4 pt-3.5 md:px-6 md:pt-2">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setModal("explore")}
            className="inline-flex items-center gap-1 font-semibold text-sm text-[#0F4146] hover:underline"
          >
            Explore Challenges
            <ChevronRightIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Showdown: one crest hard left, VS in the middle, one crest hard right.
            A 3-column grid, so each crest is pinned to its own side by the grid
            itself — there is no flex distribution to collapse and nothing is
            absolutely positioned, so the crests can NEVER stack or cross, even
            if an image fails to load. `items-end` keeps both on one baseline
            while they turn toward each other; `pt` leaves a little headroom. Row width is capped on desktop only, so a
            full-width card doesn't fling them metres apart. */}
        {/* Client Rev 4 (desktop): "Bring the two college shields closer to the VS
            circle. Constrain the shield plus VS duel cluster to about 760 to
            820px wide and keep it centered."
            Mobile keeps the 3-column grid that pins a crest to each edge. Desktop
            switches to a centred flex row with a fixed gap, which is what puts
            the crests NEXT TO the badge instead of at the far ends of a widened
            row — a `1fr` middle column would only have flung them further apart
            as the cluster grew. Still no absolute positioning and no negative
            margins, so the crests cannot cross or stack. */}
        <div className="mt-1 pt-1.5 md:pt-1 mx-auto w-full md:max-w-[790px] grid grid-cols-[auto_1fr_auto] items-end gap-2 md:flex md:justify-center md:gap-8">
          {/* Client Rev 5: "For the left USF shield, push the right inner edge
              slightly backward in perspective while bringing the left outer edge
              slightly forward." A POSITIVE rotateY does exactly that — verified
              by measuring both edges in the browser rather than assuming a
              sign. */}
          <ClubShield
            club={CLUB_SHOWDOWN.left}
            turn={SHOWDOWN_TURN}
            perspective={SHOWDOWN_PERSPECTIVE}
          />
          <span
            className="justify-self-center self-center w-9 h-9 md:w-10 md:h-10 rounded-full bg-white border border-gray-200 shadow-[0_1px_3px_rgba(15,23,42,0.10)] flex items-center justify-center font-montserrat font-bold text-[11px] md:text-xs text-[#66748C]"
            aria-hidden="true"
          >
            VS
          </span>
          {/* "For the right UT shield, push the left inner edge slightly backward
              in perspective while bringing the right outer edge slightly
              forward." — the mirror instruction, so the negated turn. */}
          <ClubShield
            club={CLUB_SHOWDOWN.right}
            turn={-SHOWDOWN_TURN}
            perspective={SHOWDOWN_PERSPECTIVE}
          />
        </div>

        {/* Broken on the sentence, not on the container width — three short
            sentences wrapping wherever the box ends read as one run-on line. */}
        <h3 className="mt-4 md:mt-2.5 text-center font-montserrat font-bold text-lg md:text-2xl leading-snug text-[#0F172A]">
          <span className="block">Your club. Your rival.</span>
          <span className="block">Your bragging rights.</span>
        </h3>

        {/* Caps + tracking, matching the design's small-label treatment. */}
        <div className="mt-2 md:mt-1.5 flex items-center justify-center gap-2.5 text-[10px] md:text-[11px] uppercase tracking-wide font-medium text-secondary">
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <BroadcastIcon /> Live markets
          </span>
          <span className="text-tertiary">•</span>
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <PrizeTrophyIcon /> Real prizes
          </span>
        </div>

        <div className="mt-4 md:mt-3 border-t border-divider">
          <button
            type="button"
            onClick={() => setModal("request")}
            className="w-full py-3.5 font-semibold text-sm md:text-base text-[#0F4146] hover:underline"
          >
            Request a Club Challenge
          </button>
        </div>
      </div>

      {modal === "explore" && (
        <ExploreChallengesModal
          onClose={() => setModal(null)}
          onRequest={() => setModal("request")}
        />
      )}

      {modal === "request" && (
        <Suspense fallback={null}>
          <ClubChallengeRequestModal
            open
            onClose={() => setModal(null)}
            user={readUser()}
          />
        </Suspense>
      )}
    </>
  );
};

// The signed-in user's own contact details, for the confirmation step. Read at
// open time rather than held in state, so it can't go stale behind the card.
const readUser = () => {
  try {
    return JSON.parse(localStorage.getItem("userInfo")) || null;
  } catch (e) {
    return null;
  }
};

// "Explore Challenges" — there is nothing to explore yet, so this says so and
// hands straight off to the request flow. Copy is the client's.
const ExploreChallengesModal = ({ onClose, onRequest }) => (
  // Opaque scrim (not a translucent panel) so nothing behind it bleeds
  // through the dialog itself.
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-[#0F172A]/60 px-4"
    role="dialog"
    aria-modal="true"
    onClick={onClose}
  >
    <div
      className="w-full max-w-md rounded-2xl bg-white p-6"
      onClick={(e) => e.stopPropagation()}
    >
      <h4 className="font-montserrat font-bold text-lg text-[#0F172A]">
        Club challenges are coming soon
      </h4>
      <p className="mt-2 text-sm text-secondary">
        Want your club in the mix? Request a challenge and we'll help get it started.
      </p>
      {/* A hand-off, not a dead end: the one button opens the request flow.
          Dismissal is the scrim, same as before. */}
      <button
        type="button"
        onClick={onRequest}
        className="mt-5 w-full rounded-xl bg-[#0F4146] hover:bg-[#0B3A3F] text-white py-2.5 text-sm font-semibold transition-colors"
      >
        Request a Club Challenge
      </button>
    </div>
  </div>
);

export default ClubChallengeCard;
